<template>
  <div id="user-edit-tab-info">
    <div class="vx-row">
      <div class="vx-col w-full md:w-1/2">
        <!-- Col Header -->
        <div class="flex items-end">
          <feather-icon icon="UserIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('users.information') }}</span>
        </div>

        <!-- Col Content -->
        <div>
          <div class="mt-4">
            <label class="vs-input--label">{{ $t('users.birthday') }}</label>

            <date-picker
              type="date"
              v-model="data_local.details.birthday"
              element="birthday"
              view="year"
              :max="
                $moment()
                  .subtract(10, 'years')
                  .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
              "
              :min="
                $moment()
                  .subtract(100, 'years')
                  .format('YYYY-MM-DDTHH:mm:ss.SSSZ')
              "
            />
            <vs-input
              name="birthday"
              id="birthday"
              :value="
                $moment(data_local.details.birthday)
                  .locale($vs.rtl ? 'fa' : 'en')
                  .format('DD MMMM YYYY')
              "
              v-validate="'required'"
              class="w-full"
            />
            <span class="text-danger text-sm" v-show="errors.has('birthday')">
              {{ errors.first('birthday') }}
            </span>
          </div>

          <!-- Gender -->
          <div class="mt-4">
            <label class="text-sm">{{ $t('users.genders.gender') }}</label>
            <div class="mt-2">
              <vs-radio v-model="data_local.details.gender" vs-value="male" class="mr-4">{{
                $t('users.genders.male')
              }}</vs-radio>
              <vs-radio v-model="data_local.details.gender" vs-value="female" class="mr-4">{{
                $t('users.genders.female')
              }}</vs-radio>
              <vs-radio v-model="data_local.details.gender" vs-value="other">{{
                $t('users.genders.other')
              }}</vs-radio>
            </div>
          </div>

          <vs-input
            class="w-full mt-4"
            :label="$t('users.lat')"
            v-model="data_local.location.lat"
            name="lat"
          />
          <span class="text-danger text-sm" v-show="errors.has('lat')">{{ errors.first('lat') }}</span>
          <vs-input
            class="w-full mt-4"
            :label="$t('users.lon')"
            v-model="data_local.location.lon"
            name="lon"
          />
          <span class="text-danger text-sm" v-show="errors.has('lon')">{{ errors.first('lon') }}</span>
        </div>
      </div>

      <!-- Grant -->
      <div class="vx-col w-full md:w-1/2">
        <!-- Col Header -->
        <div class="flex items-end md:mt-0 mt-base">
          <feather-icon icon="UsersIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('entity.roles_and_groups') }}</span>
        </div>

        <!-- Col Content -->
        <div>
          <vs-input class="w-full mt-4" :label="$t('entity.roles')" v-model="roles" name="roles" />
          <span class="text-danger text-sm" v-show="errors.has('roles')">{{ errors.first('roles') }}</span>

          <vs-input class="w-full mt-4" :label="$t('entity.groups')" v-model="groups" name="groups" />
          <span class="text-danger text-sm" v-show="errors.has('groups')">{{ errors.first('groups') }}</span>

          <vs-input class="w-full mt-4" :label="$t('entity.tags')" v-model="tags" name="tags" />
          <span class="text-danger text-sm" v-show="errors.has('tags')">{{ errors.first('tags') }}</span>

          <vs-input class="w-full mt-4" :label="$t('entity.partners')" v-model="partners" name="partners" />
          <span class="text-danger text-sm" v-show="errors.has('partners')">{{
            errors.first('partners')
          }}</span>
        </div>
      </div>
    </div>

    <!-- Addresses Table -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <!-- Col Header -->
        <div class="flex items-end md:mt-5 mb-5 mt-base">
          <feather-icon icon="MapPinIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('users.address') }}</span>
        </div>
        <!-- Col Content -->
        <vs-table ref="table" :data="data_local.addresses">
          <template slot="thead">
            <vs-th sort-key="province">{{ $t('users.addresses.province') }}</vs-th>
            <vs-th sort-key="county">{{ $t('users.addresses.county') }}</vs-th>
            <vs-th sort-key="city">{{ $t('users.addresses.city') }}</vs-th>
            <vs-th sort-key="district">{{ $t('users.addresses.district') }}</vs-th>
            <vs-th sort-key="village">{{ $t('users.addresses.village') }}</vs-th>
            <vs-th sort-key="postal">{{ $t('users.addresses.postal') }}</vs-th>
            <vs-th sort-key="street">{{ $t('users.addresses.street') }}</vs-th>
            <vs-th sort-key="line">{{ $t('users.addresses.line') }}</vs-th>
            <vs-th sort-key="borough">{{ $t('users.addresses.borough') }}</vs-th>
            <vs-th sort-key="address">{{ $t('users.addresses.address') }}</vs-th>
            <vs-th>{{ $t('forms.actions') }}</vs-th>
          </template>

          <template slot-scope="{ data }">
            <tbody v-if="data.length">
              <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                <vs-td :data="tr.province">
                  {{ tr.province }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.province"
                      class="inputx"
                      :placeholder="$t('users.addresses.province')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.county">
                  {{ tr.county }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.county"
                      class="inputx"
                      :placeholder="$t('users.addresses.county')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.city">
                  {{ tr.city }}
                  <template slot="edit">
                    <vs-input v-model="tr.city" class="inputx" :placeholder="$t('users.addresses.city')" />
                  </template>
                </vs-td>
                <vs-td :data="tr.district">
                  {{ tr.district }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.district"
                      class="inputx"
                      :placeholder="$t('users.addresses.district')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.village">
                  {{ tr.village }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.village"
                      class="inputx"
                      :placeholder="$t('users.addresses.village')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.postal">
                  {{ tr.postal }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.postal"
                      class="inputx"
                      :placeholder="$t('users.addresses.postal')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.street">
                  {{ tr.street }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.street"
                      class="inputx"
                      :placeholder="$t('users.addresses.street')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.line">
                  {{ tr.line }}
                  <template slot="edit">
                    <vs-input v-model="tr.line" class="inputx" :placeholder="$t('users.addresses.line')" />
                  </template>
                </vs-td>
                <vs-td :data="tr.borough">
                  {{ tr.borough }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.borough"
                      class="inputx"
                      :placeholder="$t('users.addresses.borough')"
                    />
                  </template>
                </vs-td>
                <vs-td :data="tr.address">
                  {{ tr.address }}
                  <template slot="edit">
                    <vs-input
                      v-model="tr.address"
                      class="inputx"
                      :placeholder="$t('users.addresses.address')"
                    />
                  </template>
                </vs-td>
                <vs-td>
                  <feather-icon
                    icon="TrashIcon"
                    svgClasses="w-5 h-5 hover:text-danger stroke-current"
                    class="ml-2"
                    @click.stop="openConfirm(indextr)"
                  />
                </vs-td>
              </vs-tr>
            </tbody>
          </template>
        </vs-table>
      </div>
    </div>
    <!-- Add new Address Form -->
    <vs-collapse>
      <vs-collapse-item>
        <!-- Header -->
        <div slot="header">
          <feather-icon icon="PlusIcon" class="mr-2" svgClasses="w-5 h-5" />
          <span class="leading-none font-medium">{{ $t('forms.add') }} {{ $t('users.address') }}</span>
        </div>
        <div class="vx-row">
          <!-- Content-->
          <div class="vx-col w-full md:w-1/4">
            <vs-input
              v-model="new_address.province"
              :label="$t('users.addresses.province')"
              class="mt-5 w-full"
              name="new-province"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-province')">{{
              errors.first('new-province')
            }}</span>
            <vs-input
              v-model="new_address.county"
              :label="$t('users.addresses.county')"
              class="mt-5 w-full"
              name="new-county"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-county')">{{
              errors.first('new-county')
            }}</span>
          </div>
          <div class="vx-col w-full md:w-1/4">
            <vs-input
              v-model="new_address.city"
              :label="$t('users.addresses.city')"
              class="mt-5 w-full"
              name="new-city"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-city')">{{
              errors.first('new-city')
            }}</span>
            <vs-input
              v-model="new_address.district"
              :label="$t('users.addresses.district')"
              class="mt-5 w-full"
              name="new-district"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-district')">{{
              errors.first('new-district')
            }}</span>
            <vs-input
              v-model="new_address.village"
              :label="$t('users.addresses.village')"
              class="mt-5 w-full"
              name="new-village"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-village')">{{
              errors.first('new-village')
            }}</span>
          </div>
          <div class="vx-col w-full md:w-1/4">
            <vs-input
              v-model="new_address.postal"
              :label="$t('users.addresses.postal')"
              class="mt-5 w-full"
              name="new-postal"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-postal')">{{
              errors.first('new-postal')
            }}</span>
            <vs-input
              v-model="new_address.street"
              :label="$t('users.addresses.street')"
              class="mt-5 w-full"
              name="new-street"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-street')">{{
              errors.first('new-street')
            }}</span>
            <vs-input
              v-model="new_address.line"
              :label="$t('users.addresses.line')"
              class="mt-5 w-full"
              name="new-line"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-line')">{{
              errors.first('new-line')
            }}</span>
          </div>
          <div class="vx-col w-full md:w-1/4">
            <vs-input
              v-model="new_address.borough"
              :label="$t('users.addresses.borough')"
              class="mt-5 w-full"
              name="new-borough"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-borough')">{{
              errors.first('new-borough')
            }}</span>
            <vs-input
              v-model="new_address.address"
              :label="$t('users.addresses.address')"
              class="mt-5 w-full"
              name="new-address"
            />
            <span class="text-danger text-sm" v-show="errors.has('new-address')">{{
              errors.first('new-address')
            }}</span>
            <div class="mt-8 flex flex-wrap items-center">
              <vs-button
                class="ml-auto mt-2"
                @click="add_new_address"
                color="success"
                icon-pack="feather"
                icon="icon-plus"
                >{{ $t('forms.add') }}</vs-button
              >
            </div>
          </div>
        </div>
      </vs-collapse-item>
    </vs-collapse>
    <!-- Save & Reset Button -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center justify-end">
          <vs-button class="ml-auto mt-2" @click="save_changes" :disabled="!validateForm">{{
            $t('forms.update')
          }}</vs-button>
          <vs-button class="ml-4 mt-2" type="border" color="warning" @click="reset_data">{{
            $t('forms.reset')
          }}</vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      data_local: JSON.parse(JSON.stringify(this.data)),
      new_address: {
        province: null,
        county: null,
        city: null,
        district: null,
        village: null,
        postal: null,
        street: null,
        line: null,
        borough: null,
        address: null
      }
    }
  },
  computed: {
    validateForm() {
      return !this.errors.any()
    },
    roles: {
      get() {
        return this.data_local.roles.join(', ')
      },
      set(value) {
        this.data_local.roles = value.split(',').map(attr => attr.trim())
      }
    },
    groups: {
      get() {
        return this.data_local.groups.join(', ')
      },
      set(value) {
        this.data_local.groups = value.split(',').map(attr => attr.trim())
      }
    },
    tags: {
      get() {
        return this.data_local.tags.join(', ')
      },
      set(value) {
        this.data_local.tags = value.split(',').map(attr => attr.trim())
      }
    },
    partners: {
      get() {
        return this.data_local.partners.join(', ')
      },
      set(value) {
        this.data_local.partners = value.split(',').map(attr => attr.trim())
      }
    }
  },
  methods: {
    save_changes() {
      if (!this.validateForm) return
      this.$store
        .dispatch('userManagement/editUser', { id: this.data_local.id, data: this.data_local })
        // eslint-disable-next-line no-unused-vars
        .then(res => {
          this.$vs.notify({
            color: 'success',
            title: this.$t('forms.successfully_edited')
          })
        })
    },
    reset_data() {
      this.data_local = JSON.parse(JSON.stringify(this.data))
    },
    openConfirm(index) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('forms.delete'),
        text: this.$t('forms.are_you_sure'),
        accept: () => {
          delete this.data_local.addresses.splice(index, 1)
        }
      })
    },
    add_new_address() {
      this.data_local.addresses.push(this.new_address)
    }
  }
}
</script>
